import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import ServerPricingTable from "@/components/shared/ServerPricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Server, Terminal, HardDrive, Cpu, Globe, Zap } from "lucide-react";

const packages = [
  {
    name: "BDIX U-VPS 1",
    processor: "1 Core",
    ram: "2 GB",
    disk: "25 GB",
    bandwidth: "1 TB",
    originalPrice: "1,718",
    price: "859",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-1",
  },
  {
    name: "BDIX U-VPS 2",
    processor: "2 Core",
    ram: "4 GB",
    disk: "50 GB",
    bandwidth: "1 TB",
    originalPrice: "2,318",
    price: "1,159",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-2",
  },
  {
    name: "BDIX U-VPS 3",
    processor: "4 Core",
    ram: "8 GB",
    disk: "80 GB",
    bandwidth: "2 TB",
    originalPrice: "3,240",
    price: "1,620",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-3",
  },
  {
    name: "BDIX U-VPS 4",
    processor: "4 Core",
    ram: "16 GB",
    disk: "120 GB",
    bandwidth: "2 TB",
    originalPrice: "5,198",
    price: "2,599",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-4",
  },
  {
    name: "BDIX U-VPS 5",
    processor: "6 Core",
    ram: "16 GB",
    disk: "160 GB",
    bandwidth: "2 TB",
    originalPrice: "5,718",
    price: "2,859",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-5",
  },
  {
    name: "BDIX U-VPS 6",
    processor: "12 Core",
    ram: "24 GB",
    disk: "200 GB",
    bandwidth: "5 TB",
    originalPrice: "9,118",
    price: "4,559",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-unmanaged-vps/u-vps-6",
  },
];

const features = [
  { icon: Terminal, title: "Root Access", description: "Full server control" },
  { icon: Server, title: "Any OS", description: "Linux, Windows, BSD" },
  { icon: HardDrive, title: "NVMe SSD", description: "Fast BDIX storage" },
  { icon: Cpu, title: "Dedicated", description: "Guaranteed resources" },
  { icon: Globe, title: "99.99% Uptime", description: "SLA backed" },
  { icon: Zap, title: "Instant Setup", description: "Deploy in minutes" },
];

const vpsFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const UnmanagedVPS = () => {
  return (
    <Layout>
       <SEOHead
         title="Cheapest BDIX VPS Bangladesh | Unmanaged VPS with Full Root Access"
         description="Cheapest BDIX VPS in Bangladesh from ৳920/month. Cheap VPS in Bangladesh with full root access, NVMe SSD &amp; instant deployment. Cheapest VPS in Bangladesh with BDIX VPS hosting. Best VPS hosting provider in Bangladesh. BDIX VPS server."
         canonical="/bdix-vps"
         type="product"
         keywords="cheapest BDIX VPS, cheap VPS in Bangladesh, cheapest VPS in Bangladesh, BDIX VPS hosting, bdix vps server, best VPS hosting provider in Bangladesh, NVMe SSD hosting, web hosting bd, fastest BDIX VPS, bdix vps, bdix vps price in bangladesh, cheap bdix vps, best bdix vps, vps server bangladesh, vps server in bangladesh, vps hosting in bangladesh, bdix cloud vps, turbo vps"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX VPS", url: "/bdix-vps" },
        ]}
        faqs={vpsFaqs}
        products={packages.map((p) => ({
          name: `${p.name} - Cheap VPS Bangladesh`,
          description: `Cheapest BDIX VPS - ${p.processor}, ${p.ram} RAM, ${p.disk} NVMe SSD`,
          price: p.price.replace(",", ""),
        }))}
        pageName="Cheapest BDIX VPS Bangladesh - Cheap VPS Hosting BD"
        pageDescription="Cheapest BDIX VPS, cheap VPS in Bangladesh, best VPS hosting in Bangladesh. Cheapest VPS in Bangladesh from ৳920/month. BDIX VPS hosting."
      />
      <PageHero
        badge="Cheapest BDIX VPS"
        title="Cheapest VPS Hosting in Bangladesh"
        subtitle="Cheap VPS in Bangladesh with full root access and BDIX connectivity. Best VPS hosting provider in Bangladesh with NVMe SSD and instant deployment."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX VPS Hosting Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServerPricingTable
        packages={packages}
        title="Choose Your BDIX VPS Plan"
        subtitle="Full root access with your choice of operating system. Affordable Bangladesh VPS hosting price with BDIX connectivity."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default UnmanagedVPS;
